import { createBaseThemeOptions, createUnifiedTheme, palettes } from '@backstage/theme';
import { Typography } from './mui.typography';
import { tokens } from '@equinor/eds-tokens';
import { pageThemes } from './mui.page-themes';

// Based on theme.varia.css for BUI
const VariaTheme = {
  primary: '#8cd2da', // --bui-bg-solid
  secondary: '#8cd2da', // --bui-bg-solid
  // Semantic foreground/accent colors — used as .main in MUI palette (icons, borders, labels on dark surfaces)
  error: '#ffd0ce', // --bui-fg-danger
  warning: '#ffd4aa', // --bui-fg-warning
  success: '#bcf2b8', // --bui-fg-success
  info: '#b7e8ff', // --bui-fg-info
  // Semantic background fill colors — used for alert/banner backgrounds
  infoBackground: '#33607e', // --bui-bg-info
  errorBackground: '#ffa3a1', // --bui-bg-danger
  warningBackground: '#ffad63', // --bui-bg-warning
  backgroundDefault: '#132634', // --bui-bg (canvas)
  backgroundPaper: '#243746', // --bui-bg-neutral-1
  floatingMenuBackground: '#3e4f5c', // varia specific color
  chipBackground: 'lch(26.95 12.65 261.04)', // --bui-bg-neutral-2 (dark)
  chipHoverBackground: '#ace3e9', // --varia-background-color-button-hover-darkmode
  chipPressedBackground: '#c7f1f6', // --varia-background-color-button-pressed-darkmode
  chipTextColor: '#030303', // --bui-fg-solid (dark) — dark text on light teal
  text: {
    primary: '#f5fdff', // --varia-text-color-primary-darkmode
    secondary: '#d6e3ee', // --varia-text-color-secondary-darkmode
  },
  navigation: {
    background: '#0c1a24', // varia specific color
    submenu: {
      background: '#132634', // --bui-bg (canvas)
    },
  },
} as const;

export const themeDark = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      primary: {
        main: VariaTheme.primary,
      },
      secondary: {
        main: VariaTheme.secondary,
      },
      error: {
        main: VariaTheme.error,
      },
      warning: {
        main: VariaTheme.warning,
      },
      info: {
        main: VariaTheme.info,
      },
      success: {
        main: VariaTheme.success,
      },
      errorBackground: VariaTheme.errorBackground,
      warningBackground: VariaTheme.warningBackground,
      infoBackground: VariaTheme.infoBackground,
      background: {
        default: VariaTheme.backgroundDefault,
        paper: VariaTheme.backgroundPaper,
      },
      navigation: {
        background: VariaTheme.navigation.background,
        indicator: tokens.colors.interactive.primary__resting.hex,
        color: tokens.colors.text.static_icons__primary_white.hex,
        selectedColor: tokens.colors.interactive.primary__selected_highlight.hex,
        navItem: {
          hoverBackground: tokens.colors.interactive.primary__hover.hex,
        },
        submenu: {
          background: VariaTheme.navigation.submenu.background,
        },
      },
      text: {
        primary: VariaTheme.text.primary,
        secondary: VariaTheme.text.secondary,
      },
      custom: {
        sidebarGroupLabel: 'rgba(255, 255, 255, 0.6)',
        sidebarGroupLabelDivider: 'rgba(255, 255, 255, 0.12)',
      },
    },
    typography: Typography,
  }),
  pageTheme: pageThemes,
  components: {
    // fix: autocomplete background blended in with page background
    MuiAutocomplete: {
      styleOverrides: {
        paper: () => ({
          backgroundColor: VariaTheme.floatingMenuBackground,
        }),
      },
    },
    // fix: checked radio button was invisible
    MuiRadio: {
      styleOverrides: {
        root: {
          '&$checked': {
            color: `${VariaTheme.primary} !important`,
          },
        },
      },
    },
    // make the title and subtitle a bold font
    BackstageHeader: {
      styleOverrides: {
        title: () => ({
          fontWeight: 900,
        }),
        subtitle: () => ({
          fontWeight: 900,
        }),
      },
    },
    // Backstage core uses an h5 for card titles. Ideally this should be an h3 or h4
    BackstageInfoCard: {
      styleOverrides: {
        headerTitle: () => ({
          fontSize: tokens.typography.heading.h3.fontSize,
        }),
      },
    },
    // Style chips to match the Varia dark theme instead of the default MUI gray
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: VariaTheme.chipBackground, // --bui-bg-surface-2
          color: VariaTheme.text.primary,
          '&.MuiChip-clickable:hover, &.MuiChip-clickable:focus': {
            backgroundColor: VariaTheme.chipHoverBackground, // --varia-background-color-button-hover-darkmode
            color: VariaTheme.chipTextColor,
          },
          '&.MuiChip-clickable:active': {
            backgroundColor: VariaTheme.chipPressedBackground, // --varia-background-color-button-pressed-darkmode
            color: VariaTheme.chipTextColor,
          },
        },
      },
    },
    // Bring the dismissable banner component in line with Varia styling
    BackstageDismissableBanner: {
      styleOverrides: {
        info: () => ({
          backgroundColor: VariaTheme.infoBackground,
          color: '#ffffff',
          '& button': {
            color: '#ffffff',
          },
        }),
        error: () => ({
          backgroundColor: VariaTheme.errorBackground,
          color: '#1d1d1d',
          '& button': {
            color: '#1d1d1d',
          },
        }),
        message: () => ({
          backgroundColor: 'inherit',
        }),
      },
    },
  },
  defaultPageTheme: 'home',
});
